import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import useFocusTrap from "../../hooks/useFocusTrap";

/**
 * Accessible modal dialog primitive.
 *   - role="dialog", aria-modal="true"
 *   - aria-labelledby ↔ generated title id
 *   - Focus trap (cycles Tab/Shift-Tab; restores prior focus on close)
 *   - Esc closes (when closeOnEsc=true, default)
 *   - Backdrop click closes (when closeOnBackdrop=true, default)
 *   - body scroll lock while open
 *   - Portals to <body> so z-index conflicts don't matter
 *   - Mobile: full-screen on <768px; centered card on larger screens
 *
 * Replaces ad-hoc modal markup across the app. Sweeping existing modals
 * to use this is incremental — each one gets focus trap + label semantics
 * for free.
 *
 * Usage:
 *   <Modal open={isOpen} onClose={close} title="Reject candidate">
 *     <p>Body...</p>
 *     <div className="flex gap-2 justify-end mt-4">
 *       <button onClick={close}>Cancel</button>
 *       <button onClick={confirm} data-autofocus>Confirm</button>
 *     </div>
 *   </Modal>
 */
export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  closeOnEsc = true,
  closeOnBackdrop = true,
  hideCloseButton = false,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const containerRef = useFocusTrap(open);

  // Esc to close + body scroll lock — both only while open.
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && closeOnEsc) {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, closeOnEsc, onClose]);

  if (!open) return null;

  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  }[size] || "max-w-md";

  return createPortal(
    <div
      className="fixed inset-0 z-toast flex items-end md:items-center justify-center bg-slate-900/40 animate-fade-in"
      onClick={closeOnBackdrop ? onClose : undefined}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        onClick={(e) => e.stopPropagation()}
        className={`bg-white w-full ${sizeClasses} md:rounded-card rounded-t-card shadow-xl flex flex-col max-h-[90vh] focus:outline-none animate-scale-in`}
        tabIndex={-1}
      >
        {(title || !hideCloseButton) && (
          <header className="flex items-start justify-between gap-4 px-5 py-3.5 border-b border-subtle">
            <div className="flex-1 min-w-0">
              {title && (
                <h2 id={titleId} className="text-base font-semibold text-vf-base m-0">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descriptionId} className="text-xs text-vf-muted mt-0.5">
                  {description}
                </p>
              )}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                className="text-vf-muted hover:text-vf-base text-2xl leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-vf-tint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
              >
                <span aria-hidden="true">×</span>
              </button>
            )}
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="px-5 py-3 border-t border-subtle">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
